import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ArrowLeft, Printer } from "lucide-react";
import { useEffect, useId, useRef } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import type { WholesaleTicketViewModel } from "../../../../shared/contracts.js";
import { request } from "../../../api/http.js";
import { AsyncPanel } from "../../../ui/AsyncPanel.js";
import { Button, buttonAttributes } from "../../../ui/Button.js";
import { formatReceiptInstant } from "../../cases/receipt/OrderReceiptPage.js";

/**
 * The printable wholesale order ticket — the wholesale twin of
 * `OrderReceiptPage`.
 *
 * Counter staff could print a service order receipt but had no way to print a
 * wholesale order, so a wholesale customer's parts went out with nothing on
 * paper. `/wholesale/:orderId/print` renders the server's ticket projection —
 * `GET /api/wholesale/orders/:orderId/ticket`, Parts only — as one printable
 * article: a heading with the order number, a definition list of the account
 * and order facts, one table of part lines, the optional order note, and a
 * footer naming the creator and the print instant. The page shows exactly
 * what the server sent; it never reads the order detail, so nothing the
 * ticket excludes (comments, activity, email state, verification,
 * cancellation) can find its way onto paper.
 *
 * Printing is the browser's own dialog (`window.print`), which also offers
 * Save as PDF. Parts presses *Print ticket*, or arrives from *Create and
 * print* with `?autoprint=1`, which prints exactly once after the ticket has
 * rendered — never on an error state, never again on a refetch. The page
 * performs no mutation, so closing the dialog changes nothing about the
 * order. The print controls sit outside the printable container and are
 * `screen-only`; `print.css` hides them and lays the article out on US
 * Letter in black on white, reusing the receipt's own classes so both
 * documents print identically.
 */

/** The route pattern the router registers for the wholesale ticket. */
export const WHOLESALE_TICKET_ROUTE_PATH = "/wholesale/:orderId/print";

/** Also answered, so a link written as ".../ticket" lands on the same page. */
export const WHOLESALE_TICKET_ALIAS_ROUTE_PATH = "/wholesale/:orderId/ticket";

/** The query parameter that prints once the ticket has rendered. */
export const AUTOPRINT_PARAM = "autoprint";

/**
 * `/wholesale/:orderId/print`, with `?autoprint=1` when the page should print
 * as soon as the ticket is on screen (the *Create and print* destination).
 */
export function wholesaleTicketPath(orderId: string, options: { autoprint?: boolean } = {}): string {
  const base = `/wholesale/${encodeURIComponent(orderId)}/print`;
  return options.autoprint ? `${base}?${AUTOPRINT_PARAM}=1` : base;
}

/** `GET /api/wholesale/orders/:orderId/ticket` — the approved projection, nothing more. */
export function wholesaleTicketApiPath(orderId: string): string {
  return `/api/wholesale/orders/${encodeURIComponent(orderId)}/ticket`;
}

/** Under the `["wholesale", …]` root so any Parts action marks a mounted ticket stale too. */
export const wholesaleTicketQueryKey = (orderId: string) => ["wholesale", "ticket", orderId] as const;

export function fetchWholesaleTicket(orderId: string): Promise<WholesaleTicketViewModel> {
  return request<WholesaleTicketViewModel>(wholesaleTicketApiPath(orderId));
}

export function useWholesaleTicket(orderId: string): UseQueryResult<WholesaleTicketViewModel> {
  return useQuery({
    queryKey: wholesaleTicketQueryKey(orderId),
    queryFn: () => fetchWholesaleTicket(orderId),
    enabled: orderId.length > 0,
  });
}

/** Where *Back to order* goes: the Wholesale workspace with this order open. */
export function wholesaleWorkspacePath(orderId: string): string {
  return `/parts?side=wholesale&view=all&order=${encodeURIComponent(orderId)}`;
}

export function WholesaleTicketPage() {
  const { orderId = "" } = useParams<{ orderId: string }>();
  const [searchParams] = useSearchParams();
  const autoprint = searchParams.get(AUTOPRINT_PARAM) === "1";
  const query = useWholesaleTicket(orderId);
  const ticket = query.data;

  // Auto-print exactly once per order, and only once the ticket is rendered:
  // the effect runs after the article is in the DOM, the ref survives React's
  // development double-invocation and every later refetch, and an error state
  // never reaches it because `ticket` is undefined there.
  const printedForOrder = useRef<string | null>(null);
  useEffect(() => {
    if (!autoprint || !ticket || printedForOrder.current === orderId) return;
    printedForOrder.current = orderId;
    window.print();
  }, [autoprint, ticket, orderId]);

  // The print dialog's default file name is the document title.
  useEffect(() => {
    if (!ticket) return;
    const previous = document.title;
    document.title = `Wholesale order ${ticket.orderNumber}`;
    return () => {
      document.title = previous;
    };
  }, [ticket]);

  return (
    <main className="receipt-page">
      <div className="receipt-toolbar screen-only">
        <Link to={wholesaleWorkspacePath(orderId)} {...buttonAttributes({ variant: "quiet" })}>
          <span className="button__icon" aria-hidden="true">
            <ArrowLeft />
          </span>
          <span className="button__label">Back to order</span>
        </Link>
        <Button variant="primary" icon={<Printer />} onClick={() => window.print()} disabled={!ticket}>
          Print ticket
        </Button>
      </div>

      <AsyncPanel
        isLoading={query.isPending}
        error={query.isError && !ticket ? query.error : undefined}
        refreshError={query.isError && ticket ? query.error : undefined}
        refreshTitle="Couldn't refresh this ticket."
        onRetry={() => {
          void query.refetch();
        }}
        loadingLabel="Loading ticket…"
        className="receipt-panel"
      >
        {ticket ? <Ticket ticket={ticket} /> : null}
      </AsyncPanel>
    </main>
  );
}

function Ticket({ ticket }: { ticket: WholesaleTicketViewModel }) {
  const titleId = useId();
  const detailsId = useId();
  const noteId = useId();

  return (
    <article className="receipt" aria-labelledby={titleId}>
      <header className="receipt__header">
        <div>
          <p className="receipt__kicker">Wholesale order</p>
          <h1 id={titleId} className="receipt__title">
            Order ticket
          </h1>
        </div>
        <p className="receipt__case-number">{ticket.orderNumber}</p>
      </header>

      <section className="receipt__details" aria-labelledby={detailsId}>
        <h2 id={detailsId} className="visually-hidden">
          Order details
        </h2>
        <dl className="receipt__facts">
          <div className="receipt__fact">
            <dt>Created</dt>
            <dd>
              <time dateTime={ticket.createdAt}>{formatReceiptInstant(ticket.createdAt)}</time>
            </dd>
          </div>
          <div className="receipt__fact">
            <dt>Account</dt>
            <dd>{ticket.customerName}</dd>
          </div>
          {/* The customer's own PO and the RR number are optional, and an empty
              row on paper reads as a missing fact rather than an absent one —
              so each is left out entirely when the order does not carry it. */}
          {ticket.customerReference ? (
            <div className="receipt__fact">
              <dt>Customer reference</dt>
              <dd className="receipt__mono">{ticket.customerReference}</dd>
            </div>
          ) : null}
          {ticket.rrNumber ? (
            <div className="receipt__fact">
              <dt>RR number</dt>
              <dd className="receipt__mono">{ticket.rrNumber}</dd>
            </div>
          ) : null}
          {ticket.masterEtaLabel ? (
            <div className="receipt__fact">
              <dt>Master ETA</dt>
              <dd>{ticket.masterEtaLabel}</dd>
            </div>
          ) : null}
        </dl>
      </section>

      <table className="receipt__lines">
        <caption className="receipt__caption">Parts ordered</caption>
        <thead>
          <tr>
            <th scope="col">Part number</th>
            <th scope="col">Description</th>
            <th scope="col" className="receipt__numeric">
              Quantity
            </th>
            <th scope="col">ETA</th>
          </tr>
        </thead>
        <tbody>
          {ticket.lines.map((line, index) => (
            <tr key={`${index}-${line.partNumber}`}>
              <td className="receipt__mono">{line.partNumber}</td>
              <td>{line.description}</td>
              <td className="receipt__numeric">{line.quantityOrdered}</td>
              <td>{line.etaLabel}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {ticket.orderNote ? (
        <section className="receipt__note" aria-labelledby={noteId}>
          <h2 id={noteId} className="receipt__section-title">
            Order note
          </h2>
          <p className="receipt__note-body">{ticket.orderNote}</p>
        </section>
      ) : null}

      <footer className="receipt__footer">
        <p>Created by {ticket.creatorName}</p>
        <p>
          Printed <time dateTime={ticket.printedAt}>{formatReceiptInstant(ticket.printedAt)}</time>
        </p>
      </footer>
    </article>
  );
}
